import { useEffect, useMemo, useRef, useState } from 'react';
import { BadgePlus, Cable, Clock3, Trash2, X } from 'lucide-react';

const pluralize = (noun, amount) => {
  const label = noun || 'attempt';
  if (Number(amount) === 1) return label;
  if (label.endsWith('s')) return label;
  if (label.endsWith('y')) return `${label.slice(0, -1)}ies`;
  return `${label}s`;
};

const hustleIcon = (id) => id === 'streetlight_copper' ? <Cable /> : id === 'dumpster_diving' ? <Trash2 /> : <BadgePlus />;

export function HustleModal({ open, hustles, busy, onSearch, onClose }) {
  const items = Array.isArray(hustles?.items) ? hustles.items : [];
  const [selectedId, setSelectedId] = useState('dumpster_diving');
  const closeRef = useRef(onClose);
  const pushedHistoryRef = useRef(false);
  closeRef.current = onClose;
  const selected = useMemo(() => items.find((item) => item.id === selectedId) || items[0] || null, [items, selectedId]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;
    const marker = { ...(window.history.state || {}), dopeManHustlesOpen: true };
    window.history.pushState(marker, '');
    pushedHistoryRef.current = true;
    const onPopState = () => {
      pushedHistoryRef.current = false;
      closeRef.current?.();
    };
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (pushedHistoryRef.current && window.history.state?.dopeManHustlesOpen) {
        pushedHistoryRef.current = false;
        window.history.back();
      } else {
        closeRef.current?.();
      }
    };
    window.addEventListener('popstate', onPopState);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const closeSafely = () => {
    if (typeof window !== 'undefined' && pushedHistoryRef.current && window.history.state?.dopeManHustlesOpen) {
      pushedHistoryRef.current = false;
      window.history.back();
      return;
    }
    closeRef.current?.();
  };

  if (!open) return null;
  const noun = selected?.attemptNoun || 'attempt';
  return (
    <div className="hustle-overlay" role="dialog" aria-modal="true" aria-labelledby="hustle-title">
      <section className="hustle-modal glass-panel">
        <header className="hustle-header">
          <div><span className="eyebrow">MT. GATE SIDE WORK</span><h2 id="hustle-title">Hustle Some Cash</h2></div>
          <button className="hustle-close" onClick={closeSafely} aria-label="Back to Home"><X /><span>Back to Home</span></button>
        </header>
        <div className="hustle-body">
        {!items.length ? <p className="hustle-empty">No hustles are available in this run yet.</p> : <div className="hustle-layout">
          <nav className="hustle-list" aria-label="Available hustles">{items.map((item) => {
            const itemNoun = item.attemptNoun || 'attempt';
            return <button key={item.id} className={selected?.id === item.id ? 'active' : ''} onClick={() => setSelectedId(item.id)}><strong>{item.title}</strong><small>{item.ready ? `${item.attemptsRemaining} ${pluralize(itemNoun, item.attemptsRemaining)} ready` : `Cooldown · Day ${item.nextAvailableDay}`}</small></button>;
          })}</nav>
          {selected && <div className="hustle-detail">
            <div className="hustle-title-row">{hustleIcon(selected.id)}<div><h3>{selected.title}</h3><p>{selected.description}</p></div></div>
            <div className="hustle-status"><span><Clock3 /> Cooldown <b>{selected.cooldownDays} advancing day{selected.cooldownDays === 1 ? '' : 's'}</b></span><span>{pluralize(noun, 2)} left <b>{selected.attemptsRemaining}/{selected.attemptsPerCycle}</b></span>{Number(selected.cashCost || 0) > 0 && <span>Cost <b>${selected.cashCost}</b></span>}</div>
            {!selected.ready && <div className="hustle-cooldown">This hustle is cooling down. Available again on <strong>Day {selected.nextAvailableDay}</strong>.</div>}
            {selected.ready && selected.canAfford === false && <div className="hustle-cooldown">You need <strong>${selected.cashCost}</strong> cash to start this hustle.</div>}
            {selected.selectionLabel && <p className="hustle-selection-label">{selected.selectionLabel}</p>}
            <div className={`hustle-sites ${selected.sites.length === 1 ? 'single' : ''}`.trim()}>{selected.sites.map((site) => {
              const searched = selected.searchedSiteIds?.includes(site.id);
              return <button key={site.id} disabled={busy || searched || !selected.ready || selected.canAfford === false || selected.attemptsRemaining <= 0} onClick={() => onSearch(selected.id, site.id)}><strong>{site.title}</strong>{!searched && (site.riskLabel || site.payoutLabel) && <span className="hustle-site-meta">{site.riskLabel && <em>{site.riskLabel}</em>}{site.payoutLabel && <b>{site.payoutLabel}</b>}</span>}<small>{searched ? 'DONE THIS CYCLE' : site.description}</small></button>;
            })}</div>
            {selected.lastResult && <div className={`hustle-result ${selected.lastResult.tone || ''}`.trim()}><strong>{selected.lastResult.siteTitle}</strong><p>{selected.lastResult.message}</p></div>}
            <button type="button" className="hustle-footer-back" onClick={closeSafely}>Back to Home</button>
          </div>}
        </div>}
        </div>
      </section>
    </div>
  );
}
